import axios from "axios";
import { getUserAuth } from "../helpers";

export const BASE_URL = process.env["REACT_APP_SERVER_URL"];

export const OTHER_USER_ACCESS_SUB_DOMAIN = process.env["REACT_APP_OTHER_USER_ACCESS_DOMAIN"];

export const api = axios.create({
  baseURL: process.env["REACT_APP_SERVER_URL"],
});

export const whatsappApi = axios.create({
  baseURL: process.env["REACT_APP_WHATSAPP_BASE_URL"],
})


export const crossApi = axios.create({
  baseURL: process.env["REACT_APP_CROSS_BASE_URL"],
})
export const imageApi = axios.create({
  baseURL: process.env["REACT_APP_IMAGE_BASE_URL"],
})

interface PendingRequest {
  url: string;
  cancel: (message?: string) => void;
  pathname: string;
}

let activeRequests: PendingRequest[] = [];

const ALLOWED_ABORT_MODULES = [
  "/deals",
  "/complaint-report",
  "/promocode",
  "/disposition",
  "/customer-connection-details",
  "/partners"
];

const getBasePath = (url?: string): string => {
  if (!url) return "";
  return url.split("?")[0];
};

export const abortAllPendingRequests = () => {
  activeRequests.forEach((req) => {
    try {
      req.cancel("Request cancelled due to page navigation.");
    } catch (err) {
      console.error("Error cancelling request:", err);
    }
  });
  activeRequests = [];
};

const attachCancelInterceptor = (instance: any) => {
  instance.interceptors.request.use(
    (config: any) => {
      const urlPath = getBasePath(config.url);
      const requestKey = `${config.method}:${urlPath}`;
      const currentPathname = typeof window !== "undefined" ? window.location.pathname : "";

      if (urlPath) {
        // Find and abort any existing pending requests
        activeRequests = activeRequests.filter(req => {
          // Abort pending requests if the user has navigated to a different page
          if (req.pathname !== currentPathname) {
            try {
              req.cancel("Request cancelled due to page navigation.");
            } catch (err) {
              console.error("Error cancelling request:", err);
            }
            return false;
          }

          // Abort duplicate requests to the same endpoint
          if (req.url === requestKey) {
            try {
              req.cancel("Request aborted due to a newer request to the same endpoint.");
            } catch (err) {
              console.error("Error aborting duplicate request:", err);
            }
            return false;
          }
          return true;
        });
      }

      const source = axios.CancelToken.source();
      config.cancelToken = source.token;
      config.cancelRequest = source.cancel;

      activeRequests.push({
        url: requestKey,
        cancel: source.cancel,
        pathname: currentPathname
      });

      return config;
    },
    (error: any) => {
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response: any) => {
      const cancelRequest = response.config?.cancelRequest;
      if (cancelRequest) {
        activeRequests = activeRequests.filter(req => req.cancel !== cancelRequest);
      }
      return response;
    },
    (error: any) => {
      const cancelRequest = error.config?.cancelRequest;
      if (cancelRequest) {
        activeRequests = activeRequests.filter(req => req.cancel !== cancelRequest);
      }
      if (axios.isCancel(error)) {
        error.response = {
          status: 499,
          statusText: "Client Closed Request",
          data: {
            message: "Request cancelled due to page navigation.",
          },
          headers: {},
          config: error.config,
        };
      }
      return Promise.reject(error);
    }
  );
};

attachCancelInterceptor(api);
attachCancelInterceptor(whatsappApi);
attachCancelInterceptor(crossApi);
attachCancelInterceptor(imageApi);

let isAlert = false;


const logoutUser = () => {
  // Perform any necessary cleanup tasks, e.g., clearing local storage
  localStorage.clear();
  isAlert = true;

  // Redirect to the login page or perform any other required action
  window.alert(
    "Attention: Your session has expired. Please log in again to continue accessing the system. Thank you!",
  );
  window.location.href = "/auth/login"; // Replace with your desired logout destination
};

api.interceptors.request.use((config) => {
  const accessToken = getUserAuth();
  if (accessToken && config.headers) {
    if (!config.headers["auth-Token"] && !config.headers["auth-token"]) {
      config.headers["auth-Token"] = `${accessToken}`;
    }
  }
  return config;
});

crossApi.interceptors.request.use((config) => {
  const accessToken = getUserAuth();
  if (accessToken && config.headers) {
    config.headers["auth-Token"] = `${accessToken}`;
  }
  return config;
});


whatsappApi.interceptors.request.use((config) => {
  const accessToken = getUserAuth();
  if (accessToken && config.headers) {
    config.headers["auth-Key"] = `${process.env["REACT_APP_AUTH_KEY_FOR_BANQUET"]}`;
  }
  return config;
});


api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || "";
    const isLoginOrAuthRequest =
      requestUrl.includes("/authentication/login") ||
      requestUrl.includes("/user/validate-otp-get-token");
    const isAuthPage =
      typeof window !== "undefined" &&
      (window.location.pathname.includes("/auth/login") ||
        window.location.pathname.startsWith("/auth/"));

    if (error.response && error.response.status === 401) {
      if (!isAlert && !isLoginOrAuthRequest && !isAuthPage) {
        logoutUser();
      }
      // const accessTokenExpiresAt = localStorage.getItem('accessTokenExpiresAt');
      // const currentTime = new Date().getTime() / 1000; // Convert to seconds

      // if (currentTime > accessTokenExpiresAt) {
      //   // Access token has expired, trigger logout
      //   logoutUser();
      // }
    }

    return Promise.reject(error);
  },
);

// API function to create call manual
export const createCallManual = async (payload: {
  mobile_no: string;
  email: string;
  name: string;
  address: string;
  package: string;
  disposition: string;
  source: string;
  description: string;
  comment: string;
  status: string;
}) => {
  try {
    const response = await api.post('/api-create-call-manual', payload);
    return response.data;
  } catch (error) {
    console.error('Error creating call manual:', error);
    throw error;
  }
};

// Global route change listeners to abort pending requests on page navigation across the whole website
if (typeof window !== "undefined") {
  const originalPushState = window.history.pushState;
  window.history.pushState = function (...args) {
    abortAllPendingRequests();
    return originalPushState.apply(this, args);
  };

  const originalReplaceState = window.history.replaceState;
  window.history.replaceState = function (...args) {
    abortAllPendingRequests();
    return originalReplaceState.apply(this, args);
  };

  window.addEventListener("popstate", () => {
    abortAllPendingRequests();
  });
}